import {Card, DataStatus, TabProps, TabsHeader} from 'platform/components';
import {Box, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNil} from 'ramda';

import {usePairSelectableMaterialItemMutation} from '@dms/api/core';
import {useGetWarehousesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {FullScreenModal, logError} from '@dms/shared';

import {WarehouseArticleData} from '../types/WarehouseArticleData';
import {PairWarehouseArticleModalWarehouseTab} from './PairWarehouseArticleModalWarehouseTab';

interface PairWarehouseArticleModalProps {
  selectableMaterialId: string;
  onDiscard: VoidFunction;
  onPairWarehouseArticle: (warehouseArticleData: WarehouseArticleData) => Promise<void>;
  isLoadingArticle?: boolean;
}

export function PairWarehouseArticleModal(props: PairWarehouseArticleModalProps) {
  const [activeTabId, setActiveTabId] = useState(TABS_IDS.warehouseTab);

  const [warehouseArticleData, setWarehouseArticleData] = useState<WarehouseArticleData | null>(
    null
  );

  const warehousesQuery = useGetWarehousesQuery();

  const [, {isLoading: isPairing}] = usePairSelectableMaterialItemMutation({
    fixedCacheKey: 'pair-items-mutation',
  });

  const handlePairWarehouseArticle = async () => {
    if (isNil(props.selectableMaterialId)) {
      return logError('selectableMaterialId is not defined');
    }

    if (isNil(warehouseArticleData)) {
      return logError('no warehouse article selected');
    }

    await props.onPairWarehouseArticle(warehouseArticleData);
    props.onDiscard();
  };

  const tabs: TabProps[] = [
    {
      id: TABS_IDS.warehouseTab,
      title: i18n.t('entity.warehouse.labels.warehouse'),
      content: (
        <PairWarehouseArticleModalWarehouseTab
          availableWarehouses={warehousesQuery.data ?? []}
          onRowSelection={(articleId, warehouseId) =>
            setWarehouseArticleData({articleId, warehouseId})
          }
          data-testid="tabs.warehouse"
        />
      ),
    },
  ];

  const activeTabContent = tabs.find(
    (tab) => tab.id === (activeTabId ?? TABS_IDS.warehouseTab)
  )?.content;

  return (
    <FullScreenModal
      headline={i18n.t('entity.settings.labels.pairWarehouseArticle')}
      actions={[
        {
          variant: 'secondary',
          title: i18n.t('general.actions.discard'),
          onClick: props.onDiscard,
        },
        {
          isLoading: props.isLoadingArticle || isPairing,
          isDisabled: isNil(warehouseArticleData),
          title: i18n.t('entity.settings.labels.pairWarehouseArticle'),
          onClick: handlePairWarehouseArticle,
        },
      ]}
    >
      <DataStatus
        isLoading={warehousesQuery.isLoading}
        isError={warehousesQuery.isError}
        minHeight="100%"
      >
        <Box padding={4} height="100%">
          <VStack spacing={4} height="100%">
            <TabsHeader
              variant="condensed"
              tabs={tabs}
              activeTabId={activeTabId}
              onChange={setActiveTabId}
            />
            <Card isFullHeight>
              <Box height="100%" flex={1}>
                {activeTabContent}
              </Box>
            </Card>
          </VStack>
        </Box>
      </DataStatus>
    </FullScreenModal>
  );
}

const TABS_IDS = {
  warehouseTab: 'pair-warehouse-article-warehouse-tab',
};
